<template>
  <div
    class="productContainer"
    v-if="getEnhancedProductData.DisplayProductOptions"
  >
    <div class="productContainer__clear">
      <a class="productContainer__clearAllBtn" @click="clearSelections"
        >Clear All</a
      >
    </div>
    <div v-if="showCustomProductOptions">
      <CustomProductOptionsItem
        v-for="(value, name, index) in sections"
        :key="`productOption-${index}`"
        :item="value"
        :test-section-complete="index === 0"
        :componentStateFinishClicked="componentStateFinishClicked"
        :bomItemCount="bomItemCount"
        @finish-clicked="finishClicked"
        @bom-updated="bomUpdated"
      />
    </div>
    <div v-else>
      <ProductOptionsItem
        v-for="(value, name, index) in sections"
        :key="`productOption-${index}`"
        :item="value"
        :test-section-complete="index === 0"
        :componentStateFinishClicked="componentStateFinishClicked"
        :bomItemCount="bomItemCount"
        @finish-clicked="finishClicked"
        @bom-updated="bomUpdated"
      />
    </div>
    <div v-if="hasError" class="productContainer-error">
      <h1 class="productContainer-error-text">
        Oops, it appears that something went wrong. Please try again later or
        contact Customer Care to finish configuring your product.
      </h1>
    </div>
    <EnhancedSectionLoadingSkeleton />
    <EnhancedBillOfMaterials :deviceType="deviceType" />
    <BomPriceAndCartButtons :deviceType="deviceType" />
    <EnhancedProductImFinishedButton
      :item="getSaveAndContinueItem"
      :componentStateFinishClicked="componentStateFinishClicked"
      :bomItemCount="bomItemCount"
      @finish-clicked="finishClicked"
      @bom-updated="bomUpdated"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import ProductOptionsItem from "../Enhanced/ProductOptionsItem";
import CustomProductOptionsItem from "../Enhanced/CustomProductOptionsItem.vue";
import EnhancedBillOfMaterials from "../Enhanced/BillOfMaterials/EnhancedBillOfMaterials";
import BomPriceAndCartButtons from "../Enhanced/BillOfMaterials/BomPriceAndCartButtons";
import EnhancedSectionLoadingSkeleton from "../Enhanced/EnhancedSectionLoadingSkeleton";
import EnhancedProductImFinishedButton from "../Enhanced/EnhancedProductImFinishedButton";
import WecoDataApiService from "../../services/WecoDataApiService";
import { formatEnhancedMapQuoterOrderArray } from "../../legacy";

export default {
  props: {
    deviceType: {
      type: String,
      default: ""
    }
  },
  components: {
    ProductOptionsItem,
    EnhancedBillOfMaterials,
    BomPriceAndCartButtons,
    EnhancedSectionLoadingSkeleton,
    EnhancedProductImFinishedButton,
    CustomProductOptionsItem
  },
  computed: {
    ...mapState("enhancedproduct", ["sections", "hasError"]),
    ...mapGetters("enhancedproduct", [
      "getEnhancedPDPOptions",
      "getEnhancedProductData",
      "getSections",
      "getVariantCode",
      "getSaveAndContinueItem",
      "getcustomHPLFormData",
      "getSelectedOptions"
    ]),
    ...mapGetters("enhancedBom", ["getBillOfMaterials", "getBomSourceOfTruth"]),
    // this is being passed to both of the components that have I'm finished buttons
    // we need it to make them reactive when getBomSourceOfTruth is updated
    ...mapGetters("prodspecification", ["getSpecification"]),
    bomItemCount() {
      return this.getBomSourceOfTruth.length;
    },
    showCustomProductOptions() {
      const sections = this.getSections;

      if (Object.keys(sections).length < 3 && sections[2] && !sections[3]) {
        if (sections[2].IsCustomSection) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
  },
  data() {
    return {
      componentStateFinishClicked: false,
      nullCustomHPLData: {
        Id: null,
        RulesKey: null,
        ColorName: null,
        ColorNumber: null
      }
    };
  },
  methods: {
    clearSelections() {
      this.$store
        .dispatch(
          "enhancedproduct/clearSelectedOptions",
          this.getEnhancedProductData
        )
        .then(() => {
          this.$store.dispatch(
            "enhancedproduct/updatedSectionEnhancedProductOptionValues",
            {
              SectionId: "1",
              VariantCode: this.getVariantCode
            }
          );
          this.$store.dispatch(
            "enhancedBom/clearBillOfMaterials",
            this.getEnhancedProductData
          );
          // restores hasFinishedBeenClicked to its origional state
          this.$store.dispatch(
            "enhancedBom/updateHasFinishedBeenClicked",
            false
          );
          // moves active to the first image in the ProductZoomer
          this.$store.dispatch("enhancedproduct/resetToFirstImage", true);
        });
      this.$store.dispatch("enhancedproduct/updateError", false);
      this.$store.dispatch("enhancedproduct/clearProductResources");
      this.$store.dispatch("enhancedproduct/clearProductTools");
      this.$store.dispatch("prodspecification/clearProductSpecification");
      this.$store.dispatch(
        "enhancedproduct/clearProductVariantCharacteristics",
        null
      );
      this.$store.dispatch("enhancedproduct/clearCustomHPLFormData", []);
      this.$store.dispatch("enhancedproduct/clearCustomHPLData", {});
    },
    // this is fired when either of the finished buttons are clicked
    finishClicked() {
      // there are two finished clicked properties one is in the vuex store, the other is in this component
      // we need both because they are doing different things and being set to their default false at different times and places
      this.$store.dispatch("enhancedBom/updateHasFinishedBeenClicked", true);
      this.componentStateFinishClicked = true;
    },
    // This is fired only when there are bom items and componentStateFinishClicked is true
    bomUpdated() {
      // this is set to false so this call is only excuted once when finished is clicked
      this.componentStateFinishClicked = false;
      // weco post accessories to sap
      WecoDataApiService.postAccessories(
        this.getBomSourceOfTruth,
        this.getcustomHPLFormData,
        this.getSelectedOptions
      )
        .then(response => {
          // error
          if (this.postAccesoriesHasError(response)) {
            this.errorAlert();
          } else {
            // send bom items to database
            formatEnhancedMapQuoterOrderArray(
              this.getBomSourceOfTruth,
              this.$store
            );
          }
        })
        .catch(() => {
          this.errorAlert();
        });
    },
    errorAlert() {
      alert(
        "Uh oh. Something went wrong and we are unable to finish configuring your product. Please try refreshing the page or call Customer Care for assistance."
      );
    },
    // checks postAccesories call for responce errors
    postAccesoriesHasError(response) {
      // case no accessories call made
      if (null === response) {
        return false;
      }

      const successCode = "000";
      const responseCodes = response.data.status.map(status => status.code);

      return responseCodes.reduce((bool, code) => {
        if (true === bool) {
          return bool;
        }

        if (code !== successCode) {
          return true;
        }

        return bool;
      }, false);
    }
  }
};
</script>

<style lang="scss" scoped>
.productContainer {
  border-top: 3px solid rgb(229, 229, 229);
  padding-top: 15px;
  margin-bottom: 25px;

  @include media-breakpoint-up(sm) {
    border: 1px solid #d8d8d8;
    padding: 30px 20px;
  }
  @include media-breakpoint-up(lg) {
    margin-bottom: 0px;
  }

  &__clear {
    text-align: right;
    padding:5px;
  }

  &__clearAllBtn {
    color: rgb(37, 192, 208);
    font-size: 16px;
    font-family: $font-family-sans-serif-regular;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
    letter-spacing: 0.25px;
    cursor: pointer;
  }
}

.productContainer-error {
  margin: 15px 0;
}

.productContainer-error-text {
  text-align: left;
  font-family: "FFDINWebPro", Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.3;
  color: #cc0000;
}
</style>
